const HerbalDrugTechUnit1Notes = `
<div class="space-y-8 animate-fadeIn">
    
    <!-- 1. Chapter Header -->
    <div>
        <h2 class="text-2xl font-black text-green-900 mb-3 border-b-2 border-slate-200 pb-2">Unit I: Herbs, Biodynamic Agriculture & ISM</h2>
        <p class="text-slate-700 leading-relaxed text-[14px]">
            This unit provides fundamental knowledge about herbal raw materials, good agricultural practices (GAP), and the basic principles & formulations of Indian Systems of Medicine (ISM).
        </p>
    </div>

    <!-- 2. Herbs as Raw Materials -->
    <div class="bg-white p-6 border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
        <h3 class="text-[18px] font-black text-green-800 mb-4 border-b border-slate-100 pb-2">1. Herbs as Raw Materials</h3>
        
        <!-- Definitions -->
        <div class="bg-green-50 p-4 border border-green-200 rounded-lg mb-5">
            <h4 class="text-[15px] font-bold text-green-900 mb-2">Important Definitions</h4>
            <ul class="list-disc pl-5 space-y-2 text-[13px] text-slate-700">
                <li><strong>Herb:</strong> Any crude plant material (leaves, flowers, fruit, seed, stems, wood, bark, roots, rhizomes) which may be entire, fragmented, or powdered.</li>
                <li><strong>Herbal Medicine:</strong> The study or use of medicinal herbs to prevent and treat diseases and ailments or to promote health and healing.</li>
                <li><strong>Herbal Medicinal Product:</strong> Any medicinal product containing exclusively plant material or herbal drug preparations as active ingredients.</li>
                <li><strong>Herbal Drug Preparation:</strong> Comminuted or powdered herbal drugs, extracts, tinctures, fatty oils, expressed juices, or processed exudates of herbal drugs.</li>
            </ul>
        </div>

        <!-- Processing & Authentication -->
        <div>
            <h4 class="text-[15px] font-bold text-green-800 mb-2">Selection, Authentication & Processing</h4>
            <p class="text-[13px] text-slate-700 mb-2">The quality of herbal drugs depends on proper processing:</p>
            <ol class="list-decimal pl-5 space-y-2 text-[13px] text-slate-600 font-medium">
                <li><strong>Selection & Collection:</strong> Herbs must be collected at the right season/time (e.g., roots in autumn, leaves before flowering) to ensure maximum active constituents.</li>
                <li><strong>Authentication:</strong> Confirming the true identity of the plant using morphological, microscopical, chemical, and DNA profiling methods.</li>
                <li><strong>Processing:</strong>
                    <ul class="list-disc pl-5 mt-1 space-y-1 font-normal">
                        <li><em>Garbling:</em> Removal of extraneous matter, dirt, and unwanted plant parts.</li>
                        <li><em>Drying:</em> Sun drying or artificial drying to reduce moisture (<10%) and prevent enzymatic degradation/microbial growth.</li>
                        <li><em>Size Reduction & Storage:</em> Grinding to suitable mesh size and storing in airtight, light-resistant containers.</li>
                    </ul>
                </li>
            </ol>
        </div>
    </div>

    <!-- 3. Biodynamic Agriculture & GAP -->
    <div class="bg-white p-6 border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
        <h3 class="text-[18px] font-black text-green-800 mb-3">2. Biodynamic Agriculture & GAP</h3>
        <p class="text-[13px] text-slate-700 mb-4">Biodynamic agriculture is an ecological farming system that treats the farm as a cohesive, interconnected living system. It emphasizes the use of manures and composts and excludes the use of artificial chemicals.</p>
        
        <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
            <!-- Organic Farming & GAP -->
            <div class="border border-slate-200 p-4 rounded-lg bg-slate-50">
                <h4 class="text-[15px] font-bold text-green-700 mb-2">Good Agricultural Practices (GAP)</h4>
                <p class="text-[12px] text-slate-600 mb-2">GAP ensures that plant materials are produced hygienically to minimize adulteration and microbial load.</p>
                <ul class="list-disc pl-4 space-y-1 text-[12px] text-slate-700">
                    <li>Proper site selection (soil free from heavy metals).</li>
                    <li>Use of authenticated seeds/propagation material.</li>
                    <li>Controlled irrigation and fertilization (organic farming).</li>
                    <li>Hygienic harvesting and personnel health parameters.</li>
                </ul>
            </div>

            <!-- Pest Management -->
            <div class="border border-slate-200 p-4 rounded-lg bg-slate-50">
                <h4 class="text-[15px] font-bold text-green-700 mb-2">Biopesticides & Bioinsecticides</h4>
                <p class="text-[12px] text-slate-600 mb-2">Natural materials (animals, plants, bacteria) used for pest management without harming the environment.</p>
                <ul class="list-disc pl-4 space-y-1 text-[12px] text-slate-700">
                    <li><strong>Microbial Pesticides:</strong> E.g., <em>Bacillus thuringiensis</em> (Bt) produces a protein toxic to specific insects.</li>
                    <li><strong>Plant-Incorporated Protectants:</strong> Plants genetically modified to produce pesticidal substances.</li>
                    <li><strong>Biochemical Pesticides:</strong> Naturally occurring substances like Neem oil (Azadirachtin) or sex pheromones that interfere with mating.</li>
                </ul>
            </div>
        </div>
    </div>

    <!-- 4. Indian Systems of Medicine (ISM) -->
    <div class="bg-white p-6 border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
        <h3 class="text-[18px] font-black text-green-800 mb-4 border-b border-slate-100 pb-2">3. Indian Systems of Medicine (ISM)</h3>
        
        <!-- Principles of ISM -->
        <div class="mb-6">
            <h4 class="text-[16px] font-bold text-green-700 mb-3">A. Basic Principles of ISM</h4>
            <div class="overflow-x-auto rounded-lg border border-slate-200">
                <table class="min-w-full bg-white text-[13px] text-left">
                    <thead class="bg-slate-100 text-slate-700">
                        <tr>
                            <th class="px-4 py-3 font-bold w-1/4">System</th>
                            <th class="px-4 py-3 font-bold">Fundamental Principles</th>
                        </tr>
                    </thead>
                    <tbody class="divide-y divide-slate-100 text-slate-600">
                        <tr>
                            <td class="px-4 py-3 font-bold text-green-900">Ayurveda</td>
                            <td class="px-4 py-3">Based on <strong>Tridosha theory</strong> (Vata, Pitta, Kapha) and <strong>Panchamahabhuta</strong> (Earth, Water, Fire, Air, Space). Health is the equilibrium of Tridoshas.</td>
                        </tr>
                        <tr>
                            <td class="px-4 py-3 font-bold text-green-900">Siddha</td>
                            <td class="px-4 py-3">Similar to Ayurveda but heavily utilizes metals, minerals, and alchemy. Based on 96 principles (Tattvas). Agastya is considered the father of Siddha.</td>
                        </tr>
                        <tr>
                            <td class="px-4 py-3 font-bold text-green-900">Unani</td>
                            <td class="px-4 py-3">Based on the <strong>Humoral Theory</strong> of Hippocrates. The four humors are Blood (Dam), Phlegm (Balgham), Yellow bile (Safra), and Black bile (Sauda).</td>
                        </tr>
                        <tr>
                            <td class="px-4 py-3 font-bold text-green-900">Homeopathy</td>
                            <td class="px-4 py-3">Based on <strong>"Similia similibus curentur"</strong> (Let likes be cured by likes). Uses highly diluted substances to trigger the body's natural healing system. Founded by Samuel Hahnemann.</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <!-- Ayurvedic Formulations -->
        <div>
            <h4 class="text-[16px] font-bold text-green-700 mb-3">B. Ayurvedic Formulations & Standardization</h4>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div class="bg-slate-50 p-3 border border-slate-200 rounded shadow-sm">
                    <h5 class="font-bold text-green-800 text-[14px]">Aristas & Asawas</h5>
                    <p class="text-[12px] text-slate-600 mt-1">Liquid preparations formed by continuous <strong>biomedical fermentation</strong>. Asawas use cold infusion/juice, while Aristas use decoctions. Self-generated alcohol acts as a preservative.</p>
                </div>
                <div class="bg-slate-50 p-3 border border-slate-200 rounded shadow-sm">
                    <h5 class="font-bold text-green-800 text-[14px]">Churna</h5>
                    <p class="text-[12px] text-slate-600 mt-1">A fine powder of dried medicinal plants, sieved through mesh 80. Example: Triphala Churna. Standardized by particle size, moisture content, and ash value.</p>
                </div>
                <div class="bg-slate-50 p-3 border border-slate-200 rounded shadow-sm">
                    <h5 class="font-bold text-green-800 text-[14px]">Ghutika / Vati</h5>
                    <p class="text-[12px] text-slate-600 mt-1">These are pills or tablets made from one or more plant drugs mixed with a binder (like jaggery or honey). Standardized via weight variation and hardness.</p>
                </div>
                <div class="bg-slate-50 p-3 border border-slate-200 rounded shadow-sm">
                    <h5 class="font-bold text-green-800 text-[14px]">Lehya (Avaleha)</h5>
                    <p class="text-[12px] text-slate-600 mt-1">A semi-solid preparation (like Chyawanprash) made by boiling drug decoctions with sugar/jaggery until it forms a thick paste.</p>
                </div>
                <div class="bg-slate-50 p-3 border border-slate-200 rounded shadow-sm lg:col-span-2">
                    <h5 class="font-bold text-green-800 text-[14px]">Bhasma</h5>
                    <p class="text-[12px] text-slate-600 mt-1">Micro-fine, calcined powders of metals, minerals, or animal products. Prepared by extensive purification (Shodhana) and incineration (Marana) to make them biologically assimilable and non-toxic.</p>
                </div>
            </div>
        </div>
    </div>

</div>
`;

export default HerbalDrugTechUnit1Notes;